import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DRAWER_MOVEMENT_KINDS,
  type DrawerMovementKind,
  type DrawerMovementResultDto,
} from '@gc/shared';

import { ApiError } from '@/lib/api';
import { drawerMovementBody, parseWholeIqd, recordDrawerMovement } from './useCashDrawer';

/** Where `drawerMovementSchema` caps the reason. */
const NOTE_MAX = 300;

/**
 * The three mid-session movements, under the open session (owner and
 * `safe.manage`). Exported so the set is pinned by tests.
 */
export function DrawerMovementButtons({ onPick }: { onPick: (kind: DrawerMovementKind) => void }) {
  const { t } = useTranslation();
  return (
    <div data-drawer-movements="" className="mt-3 flex flex-wrap gap-2 border-t border-subtle pt-3">
      {DRAWER_MOVEMENT_KINDS.map((kind) => (
        <button
          key={kind}
          type="button"
          onClick={() => onPick(kind)}
          className="rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover max-sm:flex-1"
        >
          {t(`cashDrawer.movements.button.${kind}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * Record cash moved mid-session (owner, 2026-10-12): picked up to the safe,
 * change added from the safe, or taken by the owner. Each is a numbered safe
 * slip on the open session, so the close reads no false shortage or overage.
 *
 * The amount is positive and the reason required, as on the safe's own
 * dialogs: money leaving the till with no stated reason is what the slips
 * exist to prevent. The server refuses taking out more cash than the drawer
 * should hold (`drawer_movement_exceeds_cash`).
 */
export function DrawerMovementDialog({
  kind,
  currencySymbol,
  onClose,
  onRecorded,
}: {
  kind: DrawerMovementKind;
  currencySymbol: string;
  onClose: () => void;
  onRecorded: (result: DrawerMovementResultDto) => void;
}) {
  const { t } = useTranslation();
  const [amountRaw, setAmountRaw] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const parsed = parseWholeIqd(amountRaw);
  // Zero moves nothing: the schema wants a positive amount.
  const amountIqd = parsed !== null && parsed > 0 ? parsed : null;
  const title = t(`cashDrawer.movements.title.${kind}`);
  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (amountIqd === null || !note.trim()) return;
    setError(null);
    setSubmitting(true);
    let recorded: DrawerMovementResultDto;
    try {
      recorded = await recordDrawerMovement(drawerMovementBody(kind, amountIqd, note));
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : null;
      if (code === 'drawer_movement_exceeds_cash') {
        setError(t('cashDrawer.movements.exceeds'));
      } else if (code === 'drawer_not_enabled') {
        setError(t('cashDrawer.movements.notEnabled'));
      } else {
        setError(caught instanceof ApiError ? caught.message : t('cashDrawer.movements.failed'));
      }
      return;
    } finally {
      setSubmitting(false);
    }
    // OUTSIDE the try: the movement IS written by now. A failure in what the
    // screen does next must never read "Could not record" — the endpoint has
    // no idempotency key, so the retry would write a second pickup or take.
    onRecorded(recorded);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-content-secondary">
          {t(`cashDrawer.movements.intro.${kind}`)}
        </p>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cashDrawer.movements.amount')}
          </span>
          <div className="flex items-center gap-2">
            <input
              name="amount"
              value={amountRaw}
              onChange={(event) => setAmountRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              className={`${inputClass} tabular`}
            />
            <span className="text-sm text-content-muted">{currencySymbol}</span>
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {t('cashDrawer.movements.amountHint')}
          </span>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cashDrawer.movements.note')}
          </span>
          <input
            name="note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={NOTE_MAX}
            required
            placeholder={t('cashDrawer.movements.notePlaceholder')}
            className={inputClass}
          />
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || amountIqd === null || !note.trim()}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('cashDrawer.movements.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
